import {Helmet} from '@dr.pogodin/react-helmet';
import {Form, FormSubmitHandler} from 'platform/components';

import {useParams} from 'react-router-dom';

import {defaultTo, head, isNil} from 'ramda';
import {isUndefined} from 'ramda-adjunct';

import {
  useGetVehicleGroupQuery,
  usePatchVehicleGroupMutation,
  usePostVehicleGroupMutation,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {VehicleGroupForm} from './components/VehicleGroupForm';
import {VehicleGroupFormSchema, VehicleGroupFormValues} from './types/VehicleGroupFormSchema';

export function VehicleGroupsDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data, isLoading, isError} = useGetVehicleGroupQuery(
    {vehicleGroupId: id ?? ''},
    {skip: !id}
  );
  const [postVehicleGroup] = usePostVehicleGroupMutation();
  const [patchVehicleGroup] = usePatchVehicleGroupMutation();

  const handleSubmit: FormSubmitHandler<VehicleGroupFormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const body = {
      ...data,
      vehicleMakes: isUndefined(data.vehicleMake) ? undefined : [data.vehicleMake],
      vehicleModels: data.vehicleModels ?? undefined,
      vehicleModel_families: data.vehicleModel_families ?? undefined,
    };

    if (isNil(id)) {
      return await postVehicleGroup({
        body,
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.vehicleGroups);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await patchVehicleGroup({
      vehicleGroupId: id,
      body,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.vehicleGroups);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues: VehicleGroupFormValues = {
    name: defaultTo('', data?.name),
    vehicleType: data?.vehicleType,
    vehicleMake: head(defaultTo([], data?.vehicleMakes)),
    vehicleModels: data?.vehicleModels,
    vehicleModel_families: data?.vehicleModel_families,
  };

  const titleKey = isNil(id)
    ? 'entity.workshop.actions.newVehicleGroup'
    : 'entity.workshop.labels.vehicleGroups';

  return (
    <>
      <Helmet title={i18n.t(titleKey)} />
      <SettingsTemplate
        header={{
          title: data?.name ?? i18n.t('entity.workshop.actions.newVehicleGroup'),
          breadcrumbs: [
            {
              label: i18n.t('entity.workshop.labels.vehicleGroups'),
              href: settingsRoutes.vehicleGroups,
            },
          ],
        }}
        isLoading={isLoading}
        isError={isError}
        isCreating={isNil(id)}
        data-testid={testIds.settings.vehicleGroupsDetail('template')}
      >
        <Form<VehicleGroupFormValues>
          defaultValues={defaultValues}
          experimentalZodSchema={VehicleGroupFormSchema}
          onSubmit={handleSubmit}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi, navigateWithoutBlocker) => (
            <>
              <VehicleGroupForm control={control} formApi={formApi} />
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: i18n.t('general.actions.discardChanges'),
                    variant: 'secondary',
                    onClick: () => navigateWithoutBlocker(settingsRoutes.vehicleGroups),
                  },
                  {
                    control,
                    type: 'form-button',
                    buttonType: 'submit',
                    title: i18n.t('general.actions.saveChanges'),
                  },
                ]}
                data-testid={testIds.settings.vehicleGroupsDetail('footer')}
              />
            </>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}
